import { useReducer, useCallback, useState, useEffect } from 'react';
import { withdrawFormValidation } from './validation';
import { IWalletDetails } from '@market/interfaces/IWallet';
import { IUseWithdrawForm, IUseFormPayload, IWithdrawFees } from './IWithdrawForm';
import { toFloorFixed, catchError } from '@features/utils/functions';
import { MAIN_CURRENCY } from '@bundles/consts';
import { selectWalletTypeOptions } from '../WalletForm/WalletForm';
import SecondaryMarketService from '@services/SecondaryMarket.service';

export const useWithdrawForm = (userWallet: IWalletDetails, step: number, fees: IWithdrawFees[] | undefined): IUseWithdrawForm => {
  const initialValue = {
    errors: { initial: 'initial error disables Next button' },
    values: { country: { value: 'AL', label: 'Albania' } },
  };

  const [exchangeRate, setExchangeRate] = useState<number | undefined>(
    undefined
  );

  useEffect(() => {
    SecondaryMarketService.getExchange().then((res) => {
      setExchangeRate(
        Number(res.data[`usd_${userWallet.currency.toLowerCase()}`])
      );
    }).catch((err) => { catchError(err); });
  }, [userWallet.currency]);

  const [state, dispatch] = useReducer(useCallback((state, action) => {
    const isMainCurrency = userWallet.currency === MAIN_CURRENCY;

    const getUpdatedData = (
      payload: IUseFormPayload,
      relatedData: IUseFormPayload[]
    ) => {
      const additionalValues = setAdditionalValues(payload.name, payload.value);
      const newValues = {
        ...state.values,
        [payload.name]: payload.value,
        ...additionalValues,
        ...relatedData.reduce((acc, curr, i) => ({ ...acc, [curr.name]: curr.value }), {}),
      } as IUseWithdrawForm['values'];
      const bicSwiftAsyncError = state.errors.bic_swift_async;
      const errors = withdrawFormValidation(
        newValues,
        userWallet,
        step,
        fees,
        exchangeRate,
        payload.name === 'bic_swift'
      );
      if (bicSwiftAsyncError && payload.name !== 'bic_swift') {
        errors.bic_swift_async = bicSwiftAsyncError;
      }

      return {
        errors,
        values: newValues,
      };
    };

    switch (action.type) {
      case 'edit':
        const data = getUpdatedData(action.payload, action.relatedData || []);
        return {
          values: data.values,
          errors: data.errors,
        };

      case 'cleanSwiftExternalError':
        const { bic_swift_external, ...rest } = state.errors;
        return { ...state, errors: { ...rest } };

      case 'withdrawalAll':
        const amount = isMainCurrency
          ?  Number(toFloorFixed(userWallet.balance, 2))
          : userWallet.balance;
        const newValues = { ...state.values, amount };
        const bicSwiftAsyncError = state.errors.bic_swift_async;
        const newErrors = withdrawFormValidation(newValues, userWallet, step, fees, exchangeRate);
        return {
          ...state,
          values: newValues,
          errors: bicSwiftAsyncError ? { bic_swift_async: bicSwiftAsyncError, ...newErrors } : newErrors,
        };

      case 'setInitial':
        return initialValue;

      case 'validate':
        const newValidateValues = { ...state.values, ...action.payload };
        return {
          ...state,
          errors: withdrawFormValidation(newValidateValues, userWallet, step, fees, exchangeRate),
          values: newValidateValues,
        };

      case 'setErrors':
        return { ...state, errors: action.errors };

      case 'addedBankAccount':
        const initialBank = {
          holder_name: undefined,
          bank_address: undefined,
          account_number: undefined,
          iban: undefined,
          bic: undefined,
          sort_code: undefined,
          bank_name: undefined,
          aba: undefined,
          addressConfirmed: undefined,
        };
        return { ...state, values: { ...state.values, ...initialBank, selectedWallet: action.id } };

      case 'addedWallet':
        const initialWallet = {
          address: undefined,
          usd_wallet_type: undefined,
          bfusd_wallet_currency: undefined,
          name: undefined,
        };
        return { ...state, values: { ...state.values, ...initialWallet, selectedWallet: action.id } };

      default:
        return state;
    }
  }, [userWallet, step, exchangeRate, fees]), initialValue);

  return {
    dispatch,
    values: state.values,
    errors: state.errors,
  };
};

export const setAdditionalValues = (name: string, value: string | object | boolean) => {
  switch (name) {
    case 'bfusd_wallet_currency':
      return { usd_wallet_type: selectWalletTypeOptions[0] };
    default:
      return {};
  }
};
